










































































import { Mixins, Component } from 'vue-property-decorator';
import { AxPage } from '@/mixins';

import * as I from '@/interfaces';
import * as U from '@/utils';
import * as E from '@/enums';

import AxAPI from '@/api';

type Group = I.CodeType;
type Item = I.CodeData;

type GroupListPanel = I.AxGroupListPanel<Group, Item>;
type PageInfo = I.CodePageInfo;

type DataItem = U.DataItem<unknown, State>;
type DataStore = U.DataStore<unknown, never>;

type UpdateItemParam = I.CodeUpdateReq;

class State {
  codeType: Group;
  code: string;
  name: string;
  itemList: string[];
  itemStatus: E.ItemStatus;

  constructor() {
    this.codeType = {} as Group;
    this.code = '';
    this.name = '';
    this.itemList = [];
    this.itemStatus = E.ItemStatus.None;
  }
}

abstract class BaseClass extends Mixins(AxPage) {
  API!: AxAPI;
  panel!: GroupListPanel;
  pageInfo!: PageInfo;
  dataItem!: DataItem;
}

interface BaseInterface extends BaseClass {}

@Component
class ApiHandler extends BaseClass {
  async getPageInfo() {
    const result = await this.API.call.code.get();
    if (result instanceof Error) {
      await this.handleError(result);
      return false;
    } else {
      return result.data;
    }
  }

  async updateItem(item: UpdateItemParam) {
    const result = await this.API.call.code.codeUpdate(item);
    if (result instanceof Error) {
      await this.handleError(result);
      return false;
    } else {
      return result.data;
    }
  }
}

@Component
class DataItemHandler extends BaseClass {
  dataItems: DataItem[] = [
    {
      name: '코드',
      store: {} as DataStore,
      search: '',
      description: [],
      state: new State()
    }
  ];

  get dataItem() {
    return this.dataItems[0];
  }
}

@Component
class GroupPanelHandler extends BaseClass {
  onListGroupChanged(group?: Group) {
    if (!this.pageInfo || !group) return;

    if (this.panel.items) {
      this.panel.items = this.pageInfo.listCode.filter(x => x.dataType === group.code);
    }
  }

  onListItemChanged(item?: Item) {
    if (item) {
      const groupCode = (this.panel.group as unknown) as string;
      if (!groupCode) return;

      const codeType = this.panel.groups.find(x => x.code === groupCode);
      if (!codeType) return;

      this.dataItem.state.itemStatus = E.ItemStatus.Update;
      this.dataItem.state.codeType = codeType;
      this.dataItem.state.code = item.code;
      this.dataItem.state.name = item.name;
    } else {
      this.dataItem.state = new State();
    }
  }

  onListAddClicked(group: Group) {
    this.dataItem.state = new State();
    this.dataItem.state.codeType = group;
    this.dataItem.state.itemStatus = E.ItemStatus.New;
  }
}

@Component
class BaseData extends Mixins(AxPage) implements BaseInterface {
  dataItem!: DataItem;

  panel: GroupListPanel = {
    groups: [],
    items: [],
    group: -1,
    item: -1
  };

  listCodeType: Group[] = [
    { code: 'companycode', name: '회사 코드' },
    { code: 'deptcodea', name: '사업장 코드' },
    { code: 'deptcodeb', name: '부서 코드' },
    { code: 'dutycodea', name: '직급 코드' }
  ];

  pageInfo = {} as PageInfo;

  get listCodeNames(): string[] {
    return this.listCodeType.map(x => x.name);
  }
}

const Handlers = Mixins(ApiHandler, DataItemHandler, GroupPanelHandler);

@Component
export default class Code extends Mixins(BaseData, Handlers) {
  async prepare() {
    const pageInfo = await this.getPageInfo();

    if (pageInfo) {
      this.pageInfo = pageInfo;
      this.panel.groups = this.listCodeType;
    }
  }

  async onSaveClicked() {
    const { code, name, itemStatus } = this.dataItem.state;
    if (itemStatus === E.ItemStatus.None) return;

    const updateFlag =
      itemStatus === E.ItemStatus.New ? E.EUpdateFlag.Insert : E.EUpdateFlag.Update;

    if (!this.dataItem.state.code) {
      await this.showErrorModal('오류', '코드를 입력 해주세요.');
      return;
    } else if (!this.dataItem.state.name) {
      await this.showErrorModal('오류', '이름을 입력 해주세요.');
      return;
    }

    const title = '확인';
    const message =
      itemStatus === E.ItemStatus.New
        ? '입력한 아이템을 추가할까요?'
        : '선택한 아이템을 수정할까요?';
    const buttons = ['네', '아니오'];

    const confirm = await this.showConfirmModal(title, message, buttons);
    if (confirm !== 0) return;

    this.setLoading(true);

    const codeType = this.dataItem.state.codeType.code;
    const result = await this.updateItem({ code, name, codeType, updateFlag });

    this.setLoading(false);
    if (!result) return;

    this.onUpdated(result, updateFlag);
  }

  async onClearClicked() {
    const confirm = await this.showConfirmModal('확인', '입력중인 내용을 취소할까요?', [
      '네',
      '아니오'
    ]);

    if (confirm === 0) {
      this.dataItem.state = new State();
    }
  }

  async onDeleteClicked() {
    const { code, name, itemStatus } = this.dataItem.state;
    if (itemStatus === E.ItemStatus.None) return;

    const confirm = await this.showConfirmModal('확인', '선택된 아이템을 삭제할까요?', [
      '네',
      '아니오'
    ]);
    if (confirm !== 0) return;

    this.setLoading(true);

    const codeType = this.dataItem.state.codeType.code;
    const updateFlag = E.EUpdateFlag.Delete;
    const result = await this.updateItem({ code, name, codeType, updateFlag });

    this.setLoading(false);
    if (!result) return;
    this.onUpdated(result, updateFlag);
  }

  async onUpdated(result: Item, updateFlag: E.EUpdateFlag) {
    if (!this.pageInfo) return;
    const prevGroup = this.panel.group;

    this.panel.groups = [];
    this.panel.items = [];

    await this.prepare();

    this.panel.items = this.pageInfo.listCode.filter(
      x => x.dataType === ((this.panel.group as unknown) as string)
    );

    this.panel.group = prevGroup;
    await this.$nextTick();

    if (updateFlag !== E.EUpdateFlag.Delete) {
      this.panel.item = this.panel.items.findIndex(x => x.code === result.code);
    }

    if (updateFlag === E.EUpdateFlag.Insert) {
      this.dataItem.state.itemStatus = E.ItemStatus.Update;
      await this.showErrorModal('알림', '추가되었습니다');
    } else if (updateFlag === E.EUpdateFlag.Update) {
      await this.showErrorModal('알림', '수정되었습니다');
    } else if (updateFlag === E.EUpdateFlag.Delete) {
      this.dataItem.state = new State();
      this.panel.item = -1;
      await this.showErrorModal('알림', '삭제되었습니다');
    }
  }
}
